import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import uniqWith from 'lodash/uniqWith';

import {
  formatRole,
  loadServiceData,
  loadUser,
  toSentenceCase,
} from '../../utils';
import { getRoleIdByName, getRoleName } from '../Person/utils';

import { roleNames } from '../Person/constants';

import StyledLink from '../StyledLink';

export const isMasterPm = (user) =>
  user &&
  user.staffRoles &&
  !!user.staffRoles.find((role) => role.primary === true);

const getUserRoleLink = (roleInfo, personId) => {
  const { id, roleId, pmcId, buildingId } = roleInfo || {};
  let userRoleLink;
  switch (roleId) {
    case getRoleIdByName(roleNames.handyman):
    case getRoleIdByName(roleNames.superintendent):
    case getRoleIdByName(roleNames.property_manager):
    case getRoleIdByName(roleNames.concierge):
    case getRoleIdByName(roleNames.pmc_owner):
      userRoleLink = buildingId
        ? `/building/${buildingId}/staff/${personId}`
        : `/pmc/${pmcId}/staff/${personId}`;
      break;
    case getRoleIdByName(roleNames.tenant):
    case getRoleIdByName(roleNames.owner):
    case getRoleIdByName(roleNames.board_member):
      userRoleLink = `/building/${buildingId}/residents/${personId}`;
      break;
    case getRoleIdByName(roleNames.visitor):
    case getRoleIdByName(roleNames.broker):
    case getRoleIdByName(roleNames.prospective_tenant):
      userRoleLink = id && `/visitors/profile/${id}`;
      break;
    default:
      return null;
  }
  return userRoleLink;
};

export const getUserRoles = (entry, personId) => {
  const userRoles = entry?.roles ?? [];
  if (!Array.isArray(userRoles) || userRoles.length < 1) return null;
  if (!personId) return null;
  const roleData = userRoles?.map((role) => {
    const { propertyLevel, roleId, active } = role;
    const url = getUserRoleLink(role, personId);
    const roleName = toSentenceCase(getRoleName(roleId));
    const isPrimaryStaff = role.primaryStaff ? 'Primary Staff: ' : '';
    const propertyName = role[`${propertyLevel}Name`];
    const title = `${isPrimaryStaff}${roleName} in ${propertyName}`;
    return { url, title, active };
  });
  return uniqWith(roleData, isEqual);
};

export const getRoleBasedLinkToProperty = (role, showUnit) => {
  if (!role || isEmpty(role)) {
    return null;
  }

  const { pmcId } = role;
  const { buildingId } = role;
  const unitId = showUnit && role.unitId;

  if (pmcId && buildingId && unitId) {
    return (
      <StyledLink to={`/building/${buildingId}/units/${unitId}`}>
        {role.unitName}
      </StyledLink>
    );
  }
  if (pmcId && buildingId) {
    return (
      <StyledLink to={`/building/${buildingId}`}>
        {role.buildingName}
      </StyledLink>
    );
  }
  if (pmcId) {
    return <StyledLink to={`/pmc/${pmcId}`}>{role.pmcName}</StyledLink>;
  }
  return null;
};

export const getFilterRoleOptions = () => {
  const roles = loadServiceData().role;
  if (!roles?.length) return [];
  return roles
    .map((role) => ({
      value: role?.id,
      label: toSentenceCase(role?.name),
    }))
    .sort((a, b) => {
      if (a?.label < b?.label) return -1;
      if (a?.label > b?.label) return 1;
      return 0;
    });
};

// Roles priorities (most important on the top):
// Admin
// Master PM
// 8 - PMC Owner
// 3 - Property Manager
// 5 - Owner
// 4 - Tenant
// 10 - Prospective Tenant
// 6 - Visitor
// 9 - Broker
// 2 - Superintendent
// 1 - Handyman
// Board Member is not included

const prioritizedRoleIds = [
  getRoleIdByName(roleNames.pmc_owner),
  getRoleIdByName(roleNames.property_manager),
  getRoleIdByName(roleNames.owner),
  getRoleIdByName(roleNames.tenant),
  getRoleIdByName(roleNames.prospective_tenant),
  getRoleIdByName(roleNames.visitor),
  getRoleIdByName(roleNames.broker),
  getRoleIdByName(roleNames.superintendent),
  getRoleIdByName(roleNames.concierge),
  getRoleIdByName(roleNames.handyman),
];

export const getHighPriorityRoleId = (roleIds) => {
  const uniqueRoleIds = [...new Set(roleIds)];
  const highPriorityRoleId = prioritizedRoleIds.find((roleId) =>
    uniqueRoleIds.includes(roleId),
  );
  return highPriorityRoleId;
};

export const getHighPriorityRoleName = (roleIds) => {
  const highPriorityRoleId = getHighPriorityRoleId(roleIds);
  return formatRole(getRoleName(highPriorityRoleId));
};

// for GA report
// Roles priorities (most important on the top, omitted roles cannot log in):

// admin - no role name, check boolean
// call_center_agent - no role name, check boolean
// pmc_owner
// property_manager
// superintendent
// concierge

export const getGARoleName = (user = {}) => {
  const { id, isAdmin, isService, pmcRole, properties } = user;

  if (!id) return 'n/a';

  if (isAdmin) return 'admin';
  if (isService) return 'call_center_agent';
  if (pmcRole?.role) return getRoleName(user.pmcRole.role); // pmc_owner, property_manager

  const roleIds = properties?.map(({ roles }) => roles)?.flat();
  if (roleIds.includes(getRoleIdByName(roleNames.superintendent)))
    return 'superintendent';
  if (roleIds.includes(getRoleIdByName(roleNames.concierge)))
    return 'concierge';
  return 'n/a';
};

export const getGACompanyName = (user = {}) => {
  const { id, isAdmin, isService, pmcRole, properties } = user;

  if (!id) return 'n/a';

  if (isAdmin || isService) return 'Carson';
  if (pmcRole?.propertyName) return pmcRole.propertyName; // pmc_owner, property_manager
  return properties?.find(({ pmcName }) => Boolean(pmcName))?.pmcName || 'n/a'; // superintendent, concierge
};

export const getAllCurrentUserPropertiesIds = () => {
  const { pmcRole, pmcRoles, properties } = loadUser() || {};
  const allUserPropertiesIds = [
    pmcRole?.propertyId,
    pmcRoles?.map((role) => role?.propertyId),
    properties?.map((property) => [property?.id, property?.pmc]),
  ].flat(2);
  const uniqueUserPropertiesIds = [...new Set(allUserPropertiesIds)];
  return uniqueUserPropertiesIds;
};
